import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  TextField,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { ActionHandler } from './logic/ActionHandler';
import IconButton from '@mui/material/IconButton';

const RejectedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const estimateID = queryParams.get('estimateID');
  const token = queryParams.get('token');
  
  const [rejected, setRejected] = useState(false);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleRejectAction = async () => {
    if (!reason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await ActionHandler(estimateID, token, 'reject', reason);
      if (response.result) {
        setRejected(true);
      } else {
        setError(response.message || 'Failed to reject estimate');
      }
    } catch (error) {
      console.error('Error rejecting estimate:', error);
      setError(error.message || 'An error occurred while rejecting the estimate');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh',
      textAlign: 'center',
      padding: 3
    }}>
      {rejected ? (
        <>
          <CancelIcon color="error" sx={{ fontSize: 80, mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            Estimate Rejected
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Estimate #{estimateID} has been rejected.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 2 }}
          >
            Return to Dashboard
          </Button>
        </>
      ) : (
       <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 'auto', textAlign: 'center', position: 'relative' }}>
      <IconButton 
        onClick={() => navigate('/dashboard')}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary'
        }}
      >
        <CloseIcon />
      </IconButton>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: 'error.main'
        }}
      >
        <CloseIcon sx={{ fontSize: 30, color: 'white' }} />
      </Box>
    </Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Reject Estimate #{estimateID}
      </Typography>
      
      <TextField
        label="please provide a reason for rejection...."
        multiline
        rows={6}
        fullWidth
        variant="outlined"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={{ mb: 3 }}
        error={!!error}
        helperText={error}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={handleRejectAction}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 160 }}
        >
          {isSubmitting ? 'Submitting...' : 'Confirm Rejection'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </Box>
    </Paper>
      )}
    </Box>
  );
};

export default RejectedPage;